import _ from 'lodash';
import { getWorkMinutesPerDay } from '../util/dates';

const ANALYSIS_MIN_CARDS_COUNT = 4;
const ANALYSIS_DEFAULT_CARDS_COUNT = 20;
const ANALYSIS_STATUS = {
  NOT_ENOUGH_DATA: 'NotEnoughData',
  HIGH: 'High',
  LOW: 'Low',
  NORMAL: 'Normal',
  AT_RISK: 'AtRisk',
  NORMAL_RARE: 'NormalRare',
  LOW_RARE: 'LowRare',
  HIGH_RARE: 'HighRare'
};

function analyseWIPData(data, flowMetric, dateOrderField, excludeWeekendOpts, useWorkingHoursInCycleTimeDays) {
  if (data.length < ANALYSIS_MIN_CARDS_COUNT || _.sumBy(data, flowMetric) === 0) {
    return {
      status: ANALYSIS_STATUS.NOT_ENOUGH_DATA,
      signals: []
    };
  }

  const dataForAnalysis = getDataForAnalysis(data, ANALYSIS_DEFAULT_CARDS_COUNT, dateOrderField);

  let analysisResult = detectSignals(dataForAnalysis, flowMetric);

  let result = {
    ...analysisResult
  };

  if (analysisResult.status !== ANALYSIS_STATUS.NORMAL && analysisResult.status !== ANALYSIS_STATUS.NOT_ENOUGH_DATA) {
    const latestData = _.last(analysisResult.baselineCards);
    const signal = analysisResult.signals && analysisResult.signals.length > 0 ? analysisResult.signals[0] : undefined;

    if (latestData && signal && latestData.date !== signal.date) {
      result.status = ANALYSIS_STATUS.NORMAL;
      const threeSigmaResult = threeSigmaLimitAnalysisResult(dataForAnalysis, flowMetric);
      result.signals = [];
      result.upperLimit = threeSigmaResult.upperLimit;
      result.lowerLimit = threeSigmaResult.lowerLimit;
    }
  }

  if (shouldNormalizeHighStatus(result, flowMetric, flowMetric === 'totalWipAge', excludeWeekendOpts, useWorkingHoursInCycleTimeDays)) {
      result.status = ANALYSIS_STATUS.NORMAL;
  }
  return result;
}

function shouldNormalizeHighStatus(result, flowMetric, isAge, excludeWeekendOpts, useWorkingHoursInCycleTimeDays) {
  if (result.status !== ANALYSIS_STATUS.HIGH || result.signals.length === 0) {
    return false;
  }

  const minutesPerDay = getWorkMinutesPerDay(excludeWeekendOpts, useWorkingHoursInCycleTimeDays);
  const upperLimitDays = isAge? Math.ceil(result.upperLimit / minutesPerDay): Math.ceil(10 * result.upperLimit) / 10;
  const lowerLimitDays = isAge? Math.ceil(result.lowerLimit / minutesPerDay): Math.ceil(10 * result.lowerLimit) / 10;
  const signalValueDays = isAge? Math.ceil(result.signals[0][flowMetric] / minutesPerDay): Math.ceil(10 * result.signals[0][flowMetric]) / 10;

  // When limits and signal value are all within the same day range, consider it normal
  return upperLimitDays === lowerLimitDays && upperLimitDays === signalValueDays;
}

function analyse(data, flowMetric, dateOrderField, excludeWeekendOpts, useWorkingHoursInCycleTimeDays) {
  if (data.length < ANALYSIS_MIN_CARDS_COUNT) {
    return {
      status: ANALYSIS_STATUS.NOT_ENOUGH_DATA,
      signals: []
    };
  }

  const dataForAnalysis = getDataForAnalysis(data, ANALYSIS_DEFAULT_CARDS_COUNT, dateOrderField);

  const result = detectSignals(dataForAnalysis, flowMetric);
  if (shouldNormalizeHighStatus(result, flowMetric, flowMetric === 'cycleTimeInMinutes', excludeWeekendOpts, useWorkingHoursInCycleTimeDays)) {
      result.status = ANALYSIS_STATUS.NORMAL;
  }
  return result;
}

function getDataForAnalysis(cards, numberOfCards, dateOrderField) {
  if (cards.length >= ANALYSIS_MIN_CARDS_COUNT) {
    const sortedCards = _.orderBy(cards, [dateOrderField], ['asc']);

    return _.takeRight(sortedCards, numberOfCards);
  }

  return [];
}

function detectSignals(data, flowMetric) {
  let result = { status: ANALYSIS_STATUS.NORMAL, signals: [], baselineCards: data };

  // Rule 1: Three Sigma Rule
  const threeSigmaResult = threeSigmaLimitAnalysisResult(data, flowMetric);

  if (threeSigmaResult.status !== ANALYSIS_STATUS.NORMAL) {
    return handleSignalDetection(result, data, flowMetric, threeSigmaResult);
  }

  // Rule 2: Two Out Of Three Sigma Rule
  const dr2Result = twoOutOfThreeSigmaRule(data, flowMetric);

  if (dr2Result.status !== ANALYSIS_STATUS.NORMAL) {
    return handleSignalDetection(result, data, flowMetric, dr2Result);
  }

  // Rule 3: Four Out Of Five Sigma Rule
  const dr3Result = fourOutOfFiveSigmaRule(data, flowMetric);

  if (dr3Result.status !== ANALYSIS_STATUS.NORMAL) {
    return handleSignalDetection(result, data, flowMetric, dr3Result);
  }

  // Rule 4: Eight Successive Values Rule
  const dr4Result = eightSuccessiveValuesRule(data, flowMetric);

  if (dr4Result.status !== ANALYSIS_STATUS.NORMAL) {
    return handleSignalDetection(result, data, flowMetric, dr4Result);
  }
  else {
    result.upperLimit = threeSigmaResult.upperLimit;
    result.lowerLimit = threeSigmaResult.lowerLimit;
    return result;
  }
}

function handleSignalDetection(result, data, flowMetric, ruleResult) {
  const cardsAfterSignalCount = data.length - ruleResult.index - 1;

  if (cardsAfterSignalCount < ANALYSIS_MIN_CARDS_COUNT) {
    // Consider there is a signal
    result.status = ruleResult.status;
    result.signals = ruleResult.item ? [ruleResult.item] : ruleResult.items;
    result.upperLimit = ruleResult.upperLimit;
    result.lowerLimit = ruleResult.lowerLimit;
  } else {
    // Recalculate by changing the baseline
    result = changeBaselineAndRecalculate(data, cardsAfterSignalCount, flowMetric);
  }

  return result;
}

function changeBaselineAndRecalculate(data, cardsCount, flowMetric) {
  const cardsForAnalysis = _.takeRight(data, cardsCount);

  return detectSignals(cardsForAnalysis, flowMetric);
}

function threeSigmaLimitAnalysisResult(data, flowMetric) {
  const avg = _.meanBy(data, flowMetric);

  const mR = _.map(_.range(data.length - 1), i => Math.abs(data[i][flowMetric] - data[i + 1][flowMetric]));

  const avgmR = _.mean(mR);

  let lowerLimit = avg - 2.66 * avgmR;
  const upperLimit = avg + 2.66 * avgmR;

  let lowSignals = [];
  if (lowerLimit > 0) {
    //lower limit signals are valid
    lowSignals = _.map(
      _.filter(data, item => {
        return item[flowMetric] < lowerLimit;
      }),
      item => ({
        status: ANALYSIS_STATUS.LOW,
        index: _.findIndex(data, item),
        item: item
      })
    );
  } else {
    lowerLimit = 0;
  }

  const highSignals = _.map(
    _.filter(data, item => {
      return item[flowMetric] > upperLimit;
    }),
    item => ({
      status: ANALYSIS_STATUS.HIGH,
      index: _.findIndex(data, item),
      item: item
    })
  );

  const allSignals = _.orderBy(_.concat(lowSignals, highSignals), ['index'], ['desc']);

  let latestSignal = getLatestSignal(data, flowMetric, allSignals);

  return allSignals.length > 0
    ? {
      ...latestSignal,
      upperLimit,
      lowerLimit
    }
    : {
      status: ANALYSIS_STATUS.NORMAL,
      upperLimit,
      lowerLimit
    };
}

function twoOutOfThreeSigmaRule(data, flowMetric) {
  const avg = _.meanBy(data, flowMetric);
  const mR = _.map(_.range(data.length - 1), i => Math.abs(data[i][flowMetric] - data[i + 1][flowMetric]));
  const avgmR = _.mean(mR);
  const diff = 2.66 * avgmR;
  // Calculate 2-sigma limits
  const upperLimit = avg + 2 * diff / 3;
  const lowerLimit = Math.max(0, avg - 2 * diff / 3);

  const signals = [];
  let signalsCount = 0;

  for (let i = 0; i < data.length - 2; i++) {

    const items = [
      { ...data[i], index: i },
      { ...data[i + 1], index: i + 1 },
      { ...data[i + 2], index: i + 2 }];

    const itemsAbove = items.filter(item => item[flowMetric] > upperLimit);
    const itemsBelow = items.filter(item => item[flowMetric] < lowerLimit);

    if (itemsAbove.length >= 2) {
      signals.push({
        status: ANALYSIS_STATUS.HIGH,
        number: signalsCount++,
        items: itemsAbove
      })
    }

    if (itemsBelow.length >= 2) {
      signals.push({
        status: ANALYSIS_STATUS.LOW,
        number: signalsCount++,
        items: itemsBelow
      })
    }
  }

  let latestSignal = getLatestSignalWithSeveralItems(data, flowMetric, signals);

  return latestSignal
    ? {
      status: latestSignal.status,
      index: _.last(latestSignal.items).index,
      items: _.orderBy(latestSignal.items, ['index'], ['desc']),
      upperLimit,
      lowerLimit
    }
    : {
      status: ANALYSIS_STATUS.NORMAL,
      upperLimit,
      lowerLimit
    };
}

function fourOutOfFiveSigmaRule(data, flowMetric) {
  const avg = _.meanBy(data, flowMetric);
  const mR = _.map(_.range(data.length - 1), i => Math.abs(data[i][flowMetric] - data[i + 1][flowMetric]));
  const avgmR = _.mean(mR);
  const diff = 2.66 * avgmR;
  // Calculate 1-sigma limits
  const upperLimit = avg + diff / 3;
  const lowerLimit = Math.max(0, avg - diff / 3);

  const signals = [];
  let signalsCount = 0;
  let latestSignal;

  if (data.length > 4) {
    for (let i = 0; i < data.length - 4; i++) {
      const items = [
        { ...data[i], index: i },
        { ...data[i + 1], index: i + 1 },
        { ...data[i + 2], index: i + 2 },
        { ...data[i + 3], index: i + 3 },
        { ...data[i + 4], index: i + 4 }
      ];

      const itemsAbove = items.filter(item => item[flowMetric] > upperLimit);
      const itemsBelow = items.filter(item => item[flowMetric] < lowerLimit);

      if (itemsAbove.length >= 4) {
        signals.push({
          status: ANALYSIS_STATUS.HIGH,
          number: signalsCount++,
          items: itemsAbove
        })
      }

      if (itemsBelow.length >= 4) {
        signals.push({
          status: ANALYSIS_STATUS.LOW,
          number: signalsCount++,
          items: itemsBelow
        })
      }
    }

    latestSignal = getLatestSignalWithSeveralItems(data, flowMetric, signals);
  }

  return latestSignal
    ? {
      status: latestSignal.status,
      index: _.last(latestSignal.items).index,
      items: _.take(_.orderBy(latestSignal.items, ['index'], ['desc']), 3),
      upperLimit,
      lowerLimit
    }
    : {
      status: ANALYSIS_STATUS.NORMAL,
      upperLimit,
      lowerLimit
    };
}

function eightSuccessiveValuesRule(data, flowMetric) {
  const avg = _.meanBy(data, flowMetric);

  const signals = [];
  let signalsCount = 0;
  let latestSignal;

  if (data.length > 7) {
    for (let i = 0; i < data.length - 7; i++) {
      const values = [
        data[i][flowMetric],
        data[i + 1][flowMetric],
        data[i + 2][flowMetric],
        data[i + 3][flowMetric],
        data[i + 4][flowMetric],
        data[i + 5][flowMetric],
        data[i + 6][flowMetric],
        data[i + 7][flowMetric]
      ];

      const allAbove = values.every(value => value > avg);
      const allBelow = values.every(value => value < avg);

      if (allAbove || allBelow) {
        signals.push({
          status: allAbove ? ANALYSIS_STATUS.HIGH : ANALYSIS_STATUS.LOW,
          number: signalsCount++,
          items: [
            { ...data[i + 7], index: i + 7 },
            { ...data[i + 6], index: i + 6 },
            { ...data[i + 5], index: i + 5 }
          ]
        });
      }
  }

    latestSignal = getLatestSignalWithSeveralItems(data, flowMetric, signals);
  }

  return latestSignal
    ? {
      status: latestSignal.status,
      index: _.last(latestSignal.items).index,
      items: latestSignal.items,
      upperLimit: avg,
      lowerLimit: 0
    }
    : {
      status: ANALYSIS_STATUS.NORMAL,
      upperLimit: avg,
      lowerLimit: 0
    };
}

function getLatestSignal(data, flowMetric, sortedSignals) {
  let latestSignal;

  if (sortedSignals.length > 0) {
    if (flowMetric === 'totalWipAge' || flowMetric === 'wip') {
      return sortedSignals[0];
    }

    return getFirstLatestSignal(data.length, sortedSignals);
  }

  return latestSignal;
}

function getFirstLatestSignal(dataLength, sortedSignals) {
  const frozenIndex = dataLength - ANALYSIS_MIN_CARDS_COUNT;

  let latestSignal = _.last(sortedSignals);

  if (latestSignal.index >= frozenIndex) {
    const sortedArray = sortedSignals.filter(signal => (signal.index >= frozenIndex));

    if (sortedArray.length > 1) {
      latestSignal = _.orderBy(sortedArray, ['index'], ['asc'])[0];
    }
  }

  return latestSignal;
}

function getLatestSignalWithSeveralItems(data, flowMetric, sortedSignals) {
  let latestSignal;

  if (sortedSignals.length > 0) {
    if (flowMetric === 'totalWipAge' || flowMetric === 'wip') {
      return sortedSignals[0];
    }

    return getFirstLatestSignalWithSeveralItems(data.length, sortedSignals);
  }

  return latestSignal;
}

function getFirstLatestSignalWithSeveralItems(dataLength, sortedSignals) {
  const frozenIndex = dataLength - ANALYSIS_MIN_CARDS_COUNT;

  let latestSignal = sortedSignals[0];

  if (_.last(latestSignal.items).index >= frozenIndex) {
    const sortedArray = sortedSignals.filter(signal => (_.last(signal.items).index >= frozenIndex));

    if (sortedArray.length > 1) {
      latestSignal = _.orderBy(sortedArray, (item) => _.last(item.items).index)[0];
    }
  }

  return latestSignal;
}

export { analyse, analyseWIPData, shouldNormalizeHighStatus, ANALYSIS_STATUS };
